import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Table, { TableProps } from 'further-ui/components/Table';
import { useSyncScroll } from 'contexts/SyncScrollContext';
import Loader from 'components/new/Loader';

type StyledTableProps = {
  isLoading?: boolean;
  externalScroll?: boolean;
  noPaddingX?: boolean;
  indentation?: number;
  headerRowFontSize?: number;
  headerRowLineHeight?: number;
  headerRowPaddingY?: number;
  bodyRowFontSize?: number;
  bodyRowLineHeight?: number;
  bodyRowPaddingY?: number;
  noTopBorder?: boolean;
  topBorderOnly?: boolean;
  bottomBorderOnly?: boolean;
  headerVerticalBorder?: boolean;
  customRowVerticalBorder?: boolean;
};

const shouldForwardProp = (prop: string) =>
  ![
    'fullHeight',
    'externalScroll',
    'noPaddingX',
    'indentation',
    'headerRowFontSize',
    'headerRowLineHeight',
    'headerRowPaddingY',
    'bodyRowFontSize',
    'bodyRowLineHeight',
    'bodyRowPaddingY',
    'noTopBorder',
    'topBorderOnly',
    'bottomBorderOnly',
    'headerVerticalBorder',
    'customRowVerticalBorder',
  ].includes(prop);

const StyledTableWrapper = styled(Box, {
  shouldForwardProp,
})<StyledTableProps & { fullHeight?: boolean }>(
  ({
    theme,
    fullHeight,
    externalScroll,
    noPaddingX,
    indentation,
    headerRowFontSize,
    headerRowLineHeight,
    headerRowPaddingY,
    bodyRowFontSize,
    bodyRowLineHeight,
    bodyRowPaddingY,
    noTopBorder,
    topBorderOnly,
    bottomBorderOnly,
    headerVerticalBorder,
    customRowVerticalBorder,
  }) => ({
    height: fullHeight ? '100%' : 'auto',
    '& .table-empty-message': {
      fontSize: '13px',
      color: theme.palette.grey[600],
    },
    '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root': {
      borderCollapse: 'separate',
      tableLayout: 'fixed',
      width: '100%',
    },
    ...(externalScroll && {
      '& .MuiTableContainer-root': {
        overflow: 'visible',
        containerType: 'normal',
      },
    }),
    ...(headerVerticalBorder === false && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:not(:last-of-type)':
        { borderRight: 'none' },
    }),
    ...(customRowVerticalBorder && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root.table-custom-footer-row > .MuiTableCell-root:not(:last-of-type)':
        { borderRight: `1px solid ${theme.palette.grey[300]}` },
    }),
    ...(indentation !== undefined && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type':
        { paddingLeft: `${indentation}px` },
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root:not(.table-custom-footer-row) > .MuiTableCell-root:first-of-type':
        { paddingLeft: `${indentation}px` },
    }),
    ...(noTopBorder && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root':
        { borderTop: 'none' },
    }),
    ...(noPaddingX && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root:not(.table-custom-footer-row) > .MuiTableCell-root':
        {
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
        },
    }),
    ...((headerRowFontSize !== undefined ||
      headerRowLineHeight !== undefined ||
      headerRowPaddingY !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root':
        {
          ...(headerRowFontSize !== undefined && {
            fontSize: `${headerRowFontSize}px`,
          }),
          ...(headerRowLineHeight !== undefined && {
            lineHeight: `${headerRowLineHeight}px`,
          }),
          ...(headerRowPaddingY !== undefined && {
            paddingTop: `${headerRowPaddingY}px`,
            paddingBottom: `${headerRowPaddingY}px`,
          }),
        },
    }),
    ...((headerRowFontSize !== undefined ||
      headerRowLineHeight !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root > *':
        {
          ...(headerRowFontSize !== undefined && {
            fontSize: `${headerRowFontSize}px`,
          }),
          ...(headerRowLineHeight !== undefined && {
            lineHeight: `${headerRowLineHeight}px`,
          }),
        },
    }),
    ...((bodyRowFontSize !== undefined ||
      bodyRowLineHeight !== undefined ||
      bodyRowPaddingY !== undefined ||
      topBorderOnly ||
      bottomBorderOnly) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root':
        {
          ...(bodyRowFontSize !== undefined && {
            fontSize: `${bodyRowFontSize}px`,
          }),
          ...(bodyRowLineHeight !== undefined && {
            lineHeight: `${bodyRowLineHeight}px`,
          }),
          ...(bodyRowPaddingY !== undefined && {
            paddingTop: `${bodyRowPaddingY}px`,
            paddingBottom: `${bodyRowPaddingY}px`,
          }),
          ...(topBorderOnly && {
            borderBottom: 'none',
          }),
          ...(bottomBorderOnly && {
            borderTop: 'none',
            borderBottom: `1px solid ${theme.palette.grey[300]} !important`,
          }),
        },
    }),
    ...(topBorderOnly && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type':
        { borderLeft: 'none' },
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:last-of-type':
        { borderRight: 'none' },
    }),
    ...(bottomBorderOnly && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type':
        { borderLeft: 'none' },
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:last-of-type':
        { borderRight: 'none' },
    }),
    ...((bodyRowFontSize !== undefined || bodyRowLineHeight !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root > *':
        {
          ...(bodyRowFontSize !== undefined && {
            fontSize: `${bodyRowFontSize}px`,
          }),
          ...(bodyRowLineHeight !== undefined && {
            lineHeight: `${bodyRowLineHeight}px`,
          }),
        },
    }),
  }),
);

const AccountingTableWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'fullHeight',
})<{ minWidth: number; fullHeight?: boolean }>(({ minWidth, fullHeight }) => ({
  height: fullHeight ? '100%' : 'auto',
  overflowX: 'auto',
  overflowY: 'hidden',
  minWidth: 0,
  containerType: 'inline-size',
  '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root': {
    minWidth,
  },
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

type Props<T extends { id: string }> = TableProps<T, string> & StyledTableProps;

const AccountingTable = <T extends { id: string }>({
  isLoading = false,
  externalScroll = true,
  noPaddingX = false,
  indentation = 24,
  headerRowFontSize = 12,
  headerRowLineHeight = 16,
  headerRowPaddingY = 12,
  bodyRowFontSize = 13,
  bodyRowLineHeight = 20,
  bodyRowPaddingY = 10,
  noTopBorder = false,
  topBorderOnly = false,
  bottomBorderOnly = false,
  headerVerticalBorder = false,
  customRowVerticalBorder = false,
  ...tableProps
}: Props<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { maxScrollWidth, registerScrollContainer, syncScroll } =
    useSyncScroll();

  useEffect(() => {
    return registerScrollContainer(scrollRef);
  }, [registerScrollContainer]);

  const styledTable = (
    <StyledTableWrapper
      fullHeight={isLoading}
      externalScroll={externalScroll}
      noPaddingX={noPaddingX}
      indentation={indentation}
      headerRowFontSize={headerRowFontSize}
      headerRowLineHeight={headerRowLineHeight}
      headerRowPaddingY={headerRowPaddingY}
      bodyRowFontSize={bodyRowFontSize}
      bodyRowLineHeight={bodyRowLineHeight}
      bodyRowPaddingY={bodyRowPaddingY}
      noTopBorder={noTopBorder}
      topBorderOnly={topBorderOnly}
      bottomBorderOnly={bottomBorderOnly}
      headerVerticalBorder={headerVerticalBorder}
      customRowVerticalBorder={customRowVerticalBorder}
    >
      {isLoading ? (
        <Loader text="Loading new data..." bordered={externalScroll} />
      ) : (
        <Table {...tableProps} />
      )}
    </StyledTableWrapper>
  );

  return externalScroll ? (
    <AccountingTableWrapper
      fullHeight={isLoading}
      minWidth={maxScrollWidth}
      ref={scrollRef}
      onScroll={(e) => syncScroll(e.currentTarget.scrollLeft, scrollRef)}
    >
      {styledTable}
    </AccountingTableWrapper>
  ) : (
    styledTable
  );
};

export default AccountingTable;
